import { hasCartId } from "./segmentCart";
import { getSegmentItems } from "./segmentFlow";

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const normalizeSegmentValue = (value) => String(value ?? "").trim();

const getValue = (item, keys) => {
  const matchedKey = keys.find((key) => hasValue(item?.[key]));

  return matchedKey ? item[matchedKey] : "";
};

const segmentKeys = ["segment_id", "segmentId", "segment"];
const businessSegmentKeys = [
  "business_segment_id",
  "cart_business_segment_id",
  "businessSegmentId",
  "business_segment",
  "businessSegment",
  "store_id",
  "storeId",
  "branch_id",
];

export const getCartSegmentInfo = (source = {}) => ({
  segment_id: getValue(source, segmentKeys),
  business_segment_id: getValue(source, businessSegmentKeys),
});

const parse = (value) => {
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return null; }
};

const getObjects = (value) => {
  const parsed = parse(value);
  if (Array.isArray(parsed)) return parsed.flatMap(getObjects);
  return parsed && typeof parsed === "object" ? [parsed] : [];
};

export const getActiveCartSegmentInfo = (cartState = {}) => {
  const cartItems = Array.isArray(cartState.cartItems) ? cartState.cartItems : [];
  const summary = cartState.serverCartSummary;
  const differentCart = hasCartId(summary?.cart_id) && hasCartId(cartState.cartId) &&
    normalizeSegmentValue(summary.cart_id) !== normalizeSegmentValue(cartState.cartId);
  const cartData = differentCart ? [] : getObjects(cartState.data);
  const nestedData = cartData.flatMap((source) =>
    ["cart", "cart_data", "cart_details", "cart_detail", "response_data", "receipt", "product_details"]
      .flatMap((key) => getObjects(source[key]))
  );
  const sources = [
    ...getObjects(summary),
    ...getObjects(summary?.product_details),
    ...cartData,
    ...nestedData,
    ...(differentCart ? [] : cartItems),
    ...(differentCart ? [] : getObjects(cartState.lastSaveCartPayload)),
  ].map(getCartSegmentInfo);

  return {
    segment_id: sources.find((source) => hasValue(source.segment_id))?.segment_id || "",
    business_segment_id: sources.find((source) => hasValue(source.business_segment_id))?.business_segment_id || "",
  };
};

const getStoreName = (source) => getValue(source, [
  "store_name", "business_segment_name", "business_name", "store_title",
]);

export const getActiveCartStoreName = (cartState = {}, storesData) => {
  const summary = cartState.serverCartSummary;
  const sameCart = !summary || (hasCartId(cartState.cartId) &&
    normalizeSegmentValue(summary.cart_id) === normalizeSegmentValue(cartState.cartId));
  const cartData = sameCart ? getObjects(cartState.data) : [];
  const sources = [
    ...getObjects(summary),
    ...getObjects(summary?.receipt),
    ...cartData,
    ...cartData.flatMap((source) =>
      ["cart", "cart_data", "cart_details", "cart_detail", "response_data", "receipt"]
        .flatMap((key) => getObjects(source[key]))
    ),
  ];
  const name = sources.map(getStoreName).find((value) => String(value).trim());
  if (name) return String(name).trim();

  const nestedStore = sources.flatMap((source) =>
    ["store", "store_details", "business_segment_details"].flatMap((key) => getObjects(source[key]))
  ).map((store) => getStoreName(store) || getValue(store, ["title", "name"]))
    .find((value) => String(value).trim());
  if (nestedStore) return String(nestedStore).trim();

  const { business_segment_id: businessId } = getActiveCartSegmentInfo(cartState);
  if (!hasValue(businessId)) return "";
  const matchingStore = getSegmentItems(storesData).find((store) =>
    normalizeSegmentValue(getValue(store, [...businessSegmentKeys, "id"])) === normalizeSegmentValue(businessId)
  );
  return String(getStoreName(matchingStore) || getValue(matchingStore, ["title", "name"]) || "").trim();
};

export const hasCartSegmentMismatch = (cartState = {}, nextCartItem = {}) => {
  const cartItems = Array.isArray(cartState.cartItems) ? cartState.cartItems : [];
  const summary = cartState.serverCartSummary;
  if (summary && (Array.isArray(summary.product_details) || summary.product_count !== undefined) &&
    !(summary.product_details?.length > 0 || Number(summary.product_count) > 0)) return false;
  const hasActiveCart =
    cartItems.length > 0 ||
    hasCartId(cartState.cartId) ||
    hasCartId(summary?.cart_id) ||
    hasCartId(cartState.data?.cart_id) ||
    hasCartId(cartState.data?.cartId) ||
    hasCartId(cartState.data?.id);

  if (!hasActiveCart) {
    return false;
  }

  const activeSegment = getActiveCartSegmentInfo(cartState);
  const nextSegment = getCartSegmentInfo(nextCartItem);
  const segmentMismatch =
    hasValue(activeSegment.segment_id) &&
    hasValue(nextSegment.segment_id) &&
    normalizeSegmentValue(activeSegment.segment_id) !== normalizeSegmentValue(nextSegment.segment_id);
  const businessSegmentMismatch =
    hasValue(activeSegment.business_segment_id) &&
    hasValue(nextSegment.business_segment_id) &&
    normalizeSegmentValue(activeSegment.business_segment_id) !==
      normalizeSegmentValue(nextSegment.business_segment_id);

  return segmentMismatch || businessSegmentMismatch;
};
